/* eslint-disable no-prototype-builtins */
import AurTooltip from '../../common/aur-tooltip/AurTooltip';
import {ReactComponent as InfoIcon} from '../../../assests/svg/info_icon.svg';
import {useDispatch} from 'react-redux';
import {Divider, IconButton, Typography} from '@mui/material';
import {setIndexingData} from '../../../redux/slices/indexingSlice';
import {useNavigate} from 'react-router-dom';
import {
  formatNumberWithComma,
  getUTCDateTimeForDisplay,
} from '../../../utils/utils';
import {formatStatus, IndexesType, indexingStatusKey, shouldShowLastIndexingEvent} from '../model';
import {useCallback, useEffect, useState} from 'react';
import {t} from 'i18next';
import {AurLightTooltip} from '../../common/aur-light-tooltip/aur-light-tooltip';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';

function IndexingRow(props: any) {
  const {trackGAEvent} = useAnalytics();
  const {ecaIndexData, showType = true} = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [standardNlpArrayData, setStandardNlpArrayData] = useState([]);
  const [customArrayData, setCustomArrayData] = useState([]);
  const standardNlpArray: any = [];

  const customArray: any = [];

  const handleIndexNavigation = (ele: any) => {
    dispatch(setIndexingData(ele));
    let location = 'custom-index';
    const status = ele?.ele?.status;
    const analyticsStatus =
      status === indexingStatusKey.inProgress
        ? indexingStatusKey.InProgress
        : (status ?? 'unknown');

    if (ele?.ele?.type === 'Standard') {
      location = 'standard-index';
    } else if (ele?.ele?.type === 'Nlp') {
      location = 'nlp-index';
    }

    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name:
          ele?.ele?.type === IndexesType.standard
            ? t('gaTracking.indexManagement.standardIndex')
            : ele?.ele?.type === IndexesType.nlp
              ? t('gaTracking.indexManagement.nlpIndex')
              : t('gaTracking.indexManagement.customIndex'),
        type: GA_CLICK_TYPES.LIST,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.indexManagement.viewedIndex'),
        details: `status: ${analyticsStatus.toLowerCase()}`,
      },
    });

    navigate(`/indexmanagement/${location}`);
  };

  function filterData(ecaIndexData: any) {
    // Arrays to hold the filtered records and their respective indices

    // Loop through the records and filter based on type
    ecaIndexData?.forEach((ele: any, index: any) => {
      if (ele.type === 'Standard' || ele.type === 'Nlp') {
        standardNlpArray.push({ele, index});
        setStandardNlpArrayData(standardNlpArray);
      } else if (ele.type === 'Custom') {
        customArray.push({ele, index});
        setCustomArrayData(customArray);
      }
    });
    // Return the filtered arrays
    return {standardNlpArray, customArray};
  }

  useEffect(() => {
    setStandardNlpArrayData([]);
    setCustomArrayData([]);
    filterData(ecaIndexData);
  }, [ecaIndexData]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, ele: any) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleIndexNavigation(ele);
    }
  };

  const getStatusClassName = useCallback((status: unknown) => {
    switch (status) {
      case indexingStatusKey.inProgress:
        return 'inprogress-color';
      case indexingStatusKey.complete:
        return 'completed-color';
      case indexingStatusKey.pending:
        return 'light-blue';
      default:
        return 'error-color';
    }
  }, []);

  const renderCustomIndexDetail = (ele: any) => {
    return (
      <div className="index-details">
        {showType && (
          <AurTooltip
            title={ele?.ele?.name?.length > 20 ? ele?.ele?.name : ''}
            slotProps={{
              popper: {
                modifiers: [{name: 'offset', options: {offset: [-300, -10]}}],
              },
            }}
          >
            <Typography
              className="typo-text subhead2_size light-blue"
              style={{
                color: '#f2f4f7', // From `.indexing-container .light-blue`
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: '28px',
                fontFamily: '"Inter"',
                letterSpacing: '0px',
              }}
            >
              {ele?.ele?.name?.length > 20
                ? `${ele?.ele?.name.slice(0, 20)}...`
                : ele?.ele?.name}
            </Typography>
          </AurTooltip>
        )}
        <div className="index-properties">
          <div className="creatted-by-container">
            <Typography className={`typo-text label2_size custom-color-eca`}>
              {t('indexes.createdBy')}
            </Typography>
            <Typography className="typo-text subhead3_size light-blue">
              {ele?.ele?.createdByUser}
            </Typography>
          </div>
          <div className="docs-count">
            <Typography className={`typo-text label2_size custom-color-eca`}>
              {t('indexes.docsInIndex')}
            </Typography>
            {ele?.ele?.hasOwnProperty('totalDocCount') ? (
              <Typography className="typo-text subhead3_size light-blue">
                {formatNumberWithComma(ele?.ele?.totalDocCount)}
              </Typography>
            ) : (
              <span
                style={{
                  color: '#f2f4f7;',
                  marginTop: '0px',
                  fontWeight: 600,
                }}
              >
                - - -
              </span>
            )}
          </div>
          {ele.type === IndexesType.standard || ele.type === IndexesType.nlp ? (
            <div className="partially-filled">
              <Typography className={`typo-text label2_size custom-color-eca `}>
                {t('indexes.partiallyFailedDocsLabel')}
              </Typography>
              {ele?.ele?.hasOwnProperty('partialFailedDocCount') ? (
                <div className="count-container">
                  <Typography className="typo-text subhead3_size custom-dis-icon">
                    {formatNumberWithComma(ele?.ele?.partialFailedDocCount)}
                  </Typography>
                  {ele?.ele?.status === indexingStatusKey.complete ? null : (
                    <AurTooltip
                      title={'Language TBD'}
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: 'offset',
                              options: {offset: [0, -6]},
                            },
                          ],
                        },
                      }}
                    >
                      <InfoIcon
                        className="info-icon-index"
                        style={{cursor: 'pointer'}}
                      />
                    </AurTooltip>
                  )}
                </div>
              ) : (
                <p
                  className="three-dash"
                  style={{color: '#A0AEC2', marginTop: '0px'}}
                >
                  - - -
                </p>
              )}
            </div>
          ) : null}
          {ele.type === IndexesType.standard || ele.type === IndexesType.nlp ? (
            <div className="completely-fielled">
              <Typography className={`typo-text label2_size custom-color-eca`}>
                {t('indexes.completelyFailedDocsLabel')}
              </Typography>
              {ele?.ele?.hasOwnProperty('totalFailedDocCount') ? (
                <div className="count-container">
                  <Typography className="typo-text subhead3_size custom-dis-icon">
                    {formatNumberWithComma(ele?.ele?.totalFailedDocCount)}
                  </Typography>
                  {ele?.ele?.status === indexingStatusKey.complete ? null : (
                    <AurTooltip
                      title={t('indexing.languageTbd')}
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: 'offset',
                              options: {offset: [0, -6]},
                            },
                          ],
                        },
                      }}
                    >
                      <InfoIcon
                        className="info-icon-index"
                        style={{cursor: 'pointer'}}
                      />
                    </AurTooltip>
                  )}
                </div>
              ) : (
                <p
                  className="three-dash"
                  style={{color: '#A0AEC2', marginTop: '0px'}}
                >
                  - - -
                </p>
              )}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderCustomIndexStatusDetail = (ele: any) => {
    return (
      <div className="status-container">
        <div className="status-details">
          <Typography
            className={`typo-text label2_size mist-color-700 custom-color-eca-status`}
          >
            {ele?.ele?.status === indexingStatusKey.inProgress ? (
              t('indexing.currentStatus')
            ) : shouldShowLastIndexingEvent(ele?.ele?.status) ? (
              <>
                {t('indexing.lastIndexingEvent')}{' '}
                <AurLightTooltip title={t('common.yearMonthDate')}>
                  <span style={{marginLeft: 4}}>
                    {getUTCDateTimeForDisplay(ele?.ele?.date)}
                  </span>
                </AurLightTooltip>
              </>
            ) : null}
          </Typography>
          <Typography
            className={`typo-text subhead3_size light-blue-eca ${getStatusClassName(
              ele?.ele?.status
            )}`}
          >
            {formatStatus(ele?.ele?.status)}
          </Typography>
        </div>
      </div>
    );
  };

  return (
    <div>
      {!!standardNlpArrayData?.length &&
        standardNlpArrayData?.map((ele: any, index: number) => {
          return (
            <div
              key={`${ele?.ele?.name}_${index}`}
              className={`indexing-container ${ele?.ele?.type === IndexesType.nlp ? 'indexing-container-nlp' : ''}`}
              style={{
                borderColor:
                  ele?.ele?.status === indexingStatusKey.inProgress
                    ? '#FFC37D'
                    : '#2d3d55',
                borderWidth: '1px',
                borderStyle: 'solid',
              }}
              data-testid={`index-row-${ele?.ele?.type}`}
              tabIndex={0}
              onClick={() => handleIndexNavigation(ele)}
              onKeyDown={(e) => handleKeyDown(e, ele)}
              aria-label={`${ele?.ele?.name}`}
            >
              <div
                className={`index-details ${ele?.ele?.type === IndexesType.nlp ? 'index-details-nlp' : ''}`}
              >
                {showType && (
                  <AurTooltip
                    title={ele?.ele?.name?.length > 20 ? ele?.ele?.name : ''}
                    slotProps={{
                      popper: {
                        modifiers: [
                          {name: 'offset', options: {offset: [-300, -10]}},
                        ],
                      },
                    }}
                  >
                    <Typography
                      className="typo-text subhead2_size light-blue"
                      style={{
                        color: '#f2f4f7', // From `.indexing-container .light-blue`
                        fontWeight: 600,
                        fontSize: '20px',
                        lineHeight: '28px',
                        fontFamily: '"Inter"',
                        letterSpacing: '0px',
                      }}
                    >
                      {ele?.ele?.name?.length > 20
                        ? `${ele?.ele?.name?.slice(0, 20)}...`
                        : ele?.ele?.name}
                    </Typography>
                  </AurTooltip>
                )}
                {ele?.ele?.type !== IndexesType.nlp && (
                  <div className="index-properties">
                    <div className="creatted-by-container">
                      <Typography
                        className={`typo-text label2_size custom-color-eca`}
                      >
                        {t('indexes.createdBy')}
                      </Typography>
                      <Typography className="typo-text subhead3_size light-blue">
                        {ele?.ele?.createdByUser}
                      </Typography>
                    </div>
                    <div className="docs-count">
                      <Typography
                        className={`typo-text label2_size custom-color-eca`}
                      >
                        {t('indexes.docsInIndex')}
                      </Typography>
                      <Typography className="typo-text subhead3_size light-blue">
                        {formatNumberWithComma(ele?.ele?.totalDocCount)}
                      </Typography>
                    </div>
                    <div className="partially-filled">
                      <Typography
                        className={`typo-text label2_size custom-color-eca `}
                      >
                        {t('indexes.partiallyFailedDocsLabel')}
                      </Typography>
                      {ele?.ele?.hasOwnProperty('partialFailedDocCount') ? (
                        <div className="count-container">
                          <Typography className="typo-text subhead3_size custom-dis-icon">
                            {formatNumberWithComma(
                              ele?.ele?.partialFailedDocCount
                            )}
                          </Typography>
                          <AurTooltip
                            title={t(`indexes.partiallyFailedDocs`)}
                            className="typo-text label3_size"
                            slotProps={{
                              popper: {
                                modifiers: [
                                  {
                                    name: 'offset',
                                    options: {offset: [0, -6]},
                                  },
                                ],
                              },
                            }}
                          >
                            <IconButton
                              className="info-icon-index"
                              style={{cursor: 'pointer'}}
                              tabIndex={0}
                              aria-label={t(
                                'accessibility.indexManagement.moreInformation'
                              )}
                              aria-describedby={'partiallyFailedHint'}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                }
                              }}
                            >
                              <InfoIcon aria-hidden="true" focusable={false} />
                            </IconButton>
                          </AurTooltip>
                          <span
                            id="partiallyFailedHint"
                            style={{display: 'none'}}
                          >
                            {t('indexes.partiallyFailedDocs')}
                          </span>
                        </div>
                      ) : (
                        <p
                          className="three-dash"
                          style={{color: '#A0AEC2', marginTop: '0px'}}
                        >
                          - - -
                        </p>
                      )}
                    </div>
                    <div className="completely-fielled">
                      <Typography
                        className={`typo-text label2_size custom-color-eca`}
                      >
                        {t('indexes.completelyFailedDocsLabel')}
                      </Typography>
                      {ele?.ele?.hasOwnProperty('totalFailedDocCount') ? (
                        <div className="count-container">
                          <Typography className="typo-text subhead3_size custom-dis-icon">
                            {formatNumberWithComma(
                              ele?.ele?.totalFailedDocCount
                            )}
                          </Typography>
                          <AurTooltip
                            className="typo-text label3_size"
                            title={t(`indexes.completedFailedDocs`)}
                            slotProps={{
                              popper: {
                                modifiers: [
                                  {
                                    name: 'offset',
                                    options: {offset: [0, -6]},
                                  },
                                ],
                              },
                            }}
                          >
                            <IconButton
                              className="info-icon-index"
                              style={{cursor: 'pointer'}}
                              tabIndex={0}
                              aria-label={t(
                                'accessibility.indexManagement.moreInformation'
                              )}
                              aria-describedby={'completelyFailedHint'}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                }
                              }}
                            >
                              <InfoIcon aria-hidden="true" focusable={false} />
                            </IconButton>
                          </AurTooltip>
                          <span
                            id="completelyFailedHint"
                            style={{display: 'none'}}
                          >
                            {t('indexes.completedFailedDocs')}
                          </span>
                        </div>
                      ) : (
                        <p
                          className="three-dash"
                          style={{color: '#A0AEC2', marginTop: '0px'}}
                        >
                          - - -
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div
                className={`status-container ${ele?.ele?.type === IndexesType.nlp ? 'status-container-nlp' : ''}`}
              >
                <div
                  className={
                    ele?.ele?.type === IndexesType.nlp ? '' : 'status-details'
                  }
                >
                  <Typography
                    className={`typo-text label2_size mist-color-700 custom-color-eca-status`}
                  >
                    {ele?.ele?.status === indexingStatusKey.inProgress ? (
                      t('indexing.currentStatus')
                    ) : shouldShowLastIndexingEvent(ele?.ele?.status) ? (
                      <>
                        {t('indexing.lastIndexingEvent')}{' '}
                        <AurLightTooltip title={t('common.yearMonthDate')}>
                          <span style={{marginLeft: 4}}>
                            {getUTCDateTimeForDisplay(ele?.ele?.date)}
                          </span>
                        </AurLightTooltip>
                      </>
                    ) : null}
                  </Typography>
                  <Typography
                    className={`typo-text subhead3_size light-blue-eca ${getStatusClassName(
                      ele?.ele?.status
                    )}`}
                  >
                    {formatStatus(ele?.ele?.status)}
                  </Typography>
                </div>
              </div>
            </div>
          );
        })}
      {customArrayData?.length > 0 ? (
        <div
          className="divider-custom"
          style={{
            marginBottom: '39px',
            marginTop: '39px',
            width: '100%',
          }}
        >
          <Divider>
            <Typography className={`typo-text label3_size custom-color-eca`}>
              {t('indexManagement.customIndexes')}
            </Typography>
          </Divider>
        </div>
      ) : null}
      {customArrayData?.length > 0 &&
        customArrayData?.map((ele: any, index: number) => {
          return (
            <div
              key={`${ele?.ele?.name}_${index}`}
              className="indexing-container"
              style={{
                borderColor:
                  ele?.ele?.status === indexingStatusKey.inProgress
                    ? '#FFC37D'
                    : '#2d3d55',
                borderWidth: '1px',
                borderStyle: 'solid',
              }}
              data-testid={`index-row-${ele?.ele?.type}`}
              tabIndex={0} // Allow row to be focusable
              onClick={() => handleIndexNavigation(ele)}
              onKeyDown={(e) => handleKeyDown(e, ele)}
              aria-label={`${ele?.ele?.name}`}
            >
              {renderCustomIndexDetail(ele)}
              {renderCustomIndexStatusDetail(ele)}
            </div>
          );
        })}
    </div>
  );
}

export default IndexingRow;
